/**
 * Pure layout of notes on a time strip: x position from time, overlap
 * filtering, and lane assignment so overlapping spans stack instead of
 * covering each other.
 */
import type { Note } from "@/api/types";

export interface NoteBox {
  note: Note;
  /** px from the strip's left edge (inset included) */
  x: number;
  /** px; ≥ minWidth */
  width: number;
  lane: number;
  isRange: boolean;
}

/** Linear time → x, clamped into [insetLeft, insetLeft + innerWidth]. */
export function noteX(t: number, from: number, to: number, innerWidth: number, insetLeft = 0): number {
  if (to <= from) return insetLeft;
  const f = (t - from) / (to - from);
  return insetLeft + Math.max(0, Math.min(1, f)) * innerWidth;
}

/** x → time (inverse of noteX), unclamped so a drag can start off-strip. */
export function xToTime(x: number, from: number, to: number, innerWidth: number, insetLeft = 0): number {
  if (innerWidth <= 0) return from;
  return from + ((x - insetLeft) / innerWidth) * (to - from);
}

export function noteOverlaps(n: Pick<Note, "at_from" | "at_to">, from: number, to: number): boolean {
  const end = n.at_to ?? n.at_from;
  return end >= from && n.at_from <= to;
}

/** Greedy lane packing: notes sorted by start; a note takes the first lane whose last box ends before it. */
export function layoutNotes(
  notes: readonly Note[],
  from: number,
  to: number,
  innerWidth: number,
  insetLeft = 0,
  minWidth = 8,
  maxLanes = 3,
): NoteBox[] {
  const visible = notes
    .filter((n) => noteOverlaps(n, from, to))
    .sort((a, b) => a.at_from - b.at_from || a.id - b.id);
  const laneEnd: number[] = [];
  const out: NoteBox[] = [];
  for (const note of visible) {
    const isRange = note.at_to !== undefined && note.at_to > note.at_from;
    const x0 = noteX(note.at_from, from, to, innerWidth, insetLeft);
    const x1 = isRange ? noteX(note.at_to!, from, to, innerWidth, insetLeft) : x0;
    const width = Math.max(minWidth, x1 - x0);
    let lane = laneEnd.findIndex((end) => end <= x0);
    if (lane === -1) {
      if (laneEnd.length < maxLanes) lane = laneEnd.length;
      else lane = laneEnd.indexOf(Math.min(...laneEnd));
    }
    laneEnd[lane] = x0 + width + 2;
    out.push({ note, x: x0, width, lane, isRange });
  }
  return out;
}

/** Notes bound to any of the given addresses (case-insensitive). */
export function notesForAddresses(notes: readonly Note[], addresses: readonly string[]): Note[] {
  const set = new Set(addresses.map((a) => a.toLowerCase()));
  return notes.filter((n) => n.address && set.has(n.address.toLowerCase()));
}

/** Parse a tags input ("a, b  c") into a deduplicated list. */
export function parseTags(raw: string): string[] {
  const out: string[] = [];
  for (const t of raw.split(/[,\s]+/)) {
    const s = t.trim().replace(/^#/, "").toLowerCase();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}
